import React, { useEffect, useState } from 'react';
import Box from '@amzn/meridian/box';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectStationCode } from '../../store/slices/shiftPlanSlice';
import { showError } from '../../store/slices/errorSlice';
import { getLeaderboard } from './api';
import { getBestSite } from '../../utils/leaderboard-utils';
import { LeaderboardData, BadgeEntry } from './model';
import { BADGE_IDS } from '../../utils/constants';
import HeroBanner from './components/HeroBanner';
import LeaderboardTable from './components/LeaderboardTable';
import RecognitionSection from './components/RecognitionSection';
import AchievementCard from './components/AchievementCard';
import './index.scss';

const LeaderboardPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const stationCode = useAppSelector(selectStationCode);

  const [leaderboardData, setLeaderboardData] = useState<LeaderboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    setLeaderboardData(null);
    getLeaderboard(stationCode || '')
      .then((data) => {
        setLeaderboardData(data);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
        dispatch(showError({
          title: 'Failed to Load Leaderboard',
          message: 'Unable to load leaderboard data. Please try again.',
        }));
      });
  }, [dispatch, stationCode]);

  // Loading state — show loader when fetching or data hasn't arrived yet (and no error)
  if (loading || (!leaderboardData && !error)) {
    return (
      <Box spacingInset="400" height="100%">
        <Row alignmentHorizontal="center" spacingInset="400">
          <Loader size="large" label="Loading leaderboard data" />
        </Row>
      </Box>
    );
  }

  // Error state — API failed
  if (error && !leaderboardData) {
    return (
      <Box spacingInset="400" height="100%">
        <Heading level={1}>Site Leaderboard</Heading>
        <Box spacingInset="400">
          <Text>Unable to load leaderboard data. Please try again later.</Text>
        </Box>
      </Box>
    );
  }

  // Empty state — API returned but no badges
  if (!leaderboardData || !leaderboardData.badges || leaderboardData.badges.length === 0) {
    return (
      <Box spacingInset="m" height="100%">
        <Heading level={1}>Site Leaderboard</Heading>
        <Box spacingInset="400">
          <Text>No leaderboard data available yet</Text>
        </Box>
      </Box>
    );
  }

  // Derive data for components
  const bestSite = getBestSite(leaderboardData.badges);
  const topSitesBadge = leaderboardData.badges.find(b => b.badgeId === BADGE_IDS.TOP_SITES);
  const entries = topSitesBadge?.entries || [];

  // Derive recognition entries — combine all sites into one entry per badge
  const consistentSitesBadge = leaderboardData.badges.find(b => b.badgeId === BADGE_IDS.CONSISTENT_SITES);
  const firstTimeAdopterBadge = leaderboardData.badges.find(b => b.badgeId === BADGE_IDS.FIRST_TIME_ADOPTER);

  const consistentSites: BadgeEntry | null = consistentSitesBadge?.entries.length
    ? {
        site: consistentSitesBadge.entries.map(e => e.site).join(', '),
        description: `Logged into RCC every week for the past month — highest engagement and adoption.`,
      }
    : null;

  const firstTimeAdopter: BadgeEntry = firstTimeAdopterBadge?.entries.length
    ? {
        site: firstTimeAdopterBadge.entries.map(e => e.site).join(', '),
        description: `OM logged first RCC session last week.`,
      }
    : {
        site: '',
        description: 'No sites onboarded last week',
      };

  return (
    <Box spacingInset="m" height="100%">
      {/* Page Header */}
      <Row alignmentVertical="top" widths={['fill', 'fit']} wrap="down">
        <Column spacing="none">
          <Heading level={1} className="leaderboard-title">Site Leaderboard</Heading>
          <Text type="b200" color="secondary">
            Week of {leaderboardData.weekOf}
          </Text>
        </Column>
        <Column spacing="none">
          <Text type="b200" color="secondary">
            Region: North America · All Sites
          </Text>
          <Text type="b200" color="secondary">
            Last refresh: {leaderboardData.generatedAt ? new Date(leaderboardData.generatedAt).toLocaleString() : 'N/A'}
          </Text>
        </Column>
      </Row>

      {/* Hero Banner */}
      {bestSite && bestSite.site && (
        <HeroBanner
          siteName={bestSite.site}
          planCount={bestSite.planCount || 0}
          daysActive={bestSite.daysActive || 0}
          dayPlans={bestSite.dayPlans || 0}
          nightPlans={bestSite.nightPlans || 0}
        />
      )}

      {/* Leaderboard Table */}
      <LeaderboardTable entries={entries} />

      {/* Bottom sections: Recognition + Achievement */}
      <Box spacingInset="400 none" className="leaderboard-bottom">
        <Row spacing="400" widths={['grid-6', 'grid-6']} alignmentVertical="top">
          <RecognitionSection
            consistentSites={consistentSites}
            firstTimeAdopter={firstTimeAdopter}
          />
          {bestSite && bestSite.site && <AchievementCard siteName={bestSite.site} />}
        </Row>
      </Box>
    </Box>
  );
};

export default LeaderboardPage;

import './index.scss';